import type { RenewalDetails } from '@policyvision/contracts';
import { useMemo } from 'react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

interface ChartDataPoint {
  date: Date;
  amount: number;
  percentageChange: number | null;
  formattedDate: string;
  year: number;
}

interface IProps {
  renewalDetails: RenewalDetails;
}

const PremiumChart = ({ renewalDetails }: IProps) => {
  const renderCustomLabel =
    (chartData: ChartDataPoint[]) => (props: unknown) => {
      const { x, y, index } = props as {
        x?: number;
        y?: number;
        index?: number;
      };
      if (!index || x === undefined || y === undefined) return null;

      const percentageChange = chartData[index]?.percentageChange;
      if (percentageChange === null || percentageChange === undefined)
        return null;

      const color = percentageChange >= 0 ? '#DC3E42' : '#2B9A66';
      const sign = percentageChange >= 0 ? '+' : '';

      return (
        <text
          x={x}
          y={y - 10}
          fill={color}
          fontSize="12"
          fontWeight="600"
          textAnchor="middle"
        >
          {`${sign}${percentageChange.toFixed(0)}%`}
        </text>
      );
    };

  const CustomTooltip = ({
    active,
    payload,
  }: {
    active?: boolean;
    payload?: unknown;
  }) => {
    if (active && payload && Array.isArray(payload) && payload.length) {
      const data = (payload[0] as { payload: ChartDataPoint }).payload;
      return (
        <div className="bg-white border border-border rounded-md shadow-lg p-3">
          <p className="font-semibold text-sm">{data.formattedDate}</p>
          <p className="text-sm">
            {new Intl.NumberFormat('fr-CA', {
              style: 'currency',
              currency: 'CAD',
            }).format(data.amount)}
          </p>
        </div>
      );
    }
    return null;
  };
  const chartData = useMemo(() => {
    return renewalDetails.primes.map((prime, index) => {
      let percentageChange = 0;
      if (index > 0) {
        const previousAmount = renewalDetails.primes[index - 1].amount;
        percentageChange =
          ((prime.amount - previousAmount) / previousAmount) * 100;
      }
      return {
        date: new Date(prime.date),
        amount: prime.amount,
        percentageChange: index > 0 ? percentageChange : null,
        formattedDate: new Date(prime.date).toLocaleDateString('fr-CA', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        }),
        year: new Date(prime.date).getFullYear(),
      };
    });
  }, [renewalDetails.primes]);
  return (
    <div>
      <ResponsiveContainer width="100%" height={268}>
        <LineChart
          data={chartData}
          margin={{ top: 30, right: 30, left: 10, bottom: 20 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis
            dataKey="year"
            stroke="#6b7280"
            fontSize={12}
            tickLine={false}
          />
          <YAxis
            stroke="#6b7280"
            fontSize={12}
            tickLine={false}
            tickFormatter={(value) => {
              if (value === 0) return '';
              return `${new Intl.NumberFormat('fr-CA').format(value)}$`;
            }}
          />
          <Tooltip content={CustomTooltip} />
          <Line
            type="monotone"
            dataKey="amount"
            stroke="#BC796C"
            strokeWidth={2}
            dot={{ fill: '#BC796C', r: 4 }}
            activeDot={{ r: 7 }}
            label={renderCustomLabel(chartData)}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default PremiumChart;
